import React, { useEffect, useState } from "react";
import {
  Radio,
  Grid,
  Input,
  Dropdown as DropdownNativo,
  Checkbox,
} from "semantic-ui-react";
import { Field, FormikProps } from "formik";
import SectionLabel from "../../../../../../components/SectionLabel";
import { FieldLabel } from "../../../../../../styles";
import { CreateCapitalizationType } from "../../Formik/types/CreateCapitalizationInitialValuesTypes";
import { maskZipCode } from "../../../../../../services/masks";
import { AddressService } from "../../../../../../services/address";
import { hirePropertyTypeOptions } from "../../../../../bond-insurance-budget-form/types";
import { PurposesEnum } from "../../../../../../enums/purposes.enum";
import CapitalizationPropertyWalletSearchDropdown, {
  WalletTag
} from "../../components/CapitalizationPropertyWalletSearchDropdown";

type CapitalizationPropertyStepProps = FormikProps<CreateCapitalizationType> & {
  estateId?: string;
};

const CapitalizationPropertyStep = ({
  values,
  setFieldValue,
  estateId,
}: CapitalizationPropertyStepProps) => {
  const [cepLoading, setCepLoading] = useState(false);
  const [noComplement, setNoComplement] = useState(
    values.property.hasComplement
  );
  const selectedFromWallet = Boolean(values.propertyPublicId);
  const isFieldLocked = selectedFromWallet;
  const resolvedEstateId = estateId || values.estateId;

  const onChangeCep = (zipCode: string): void => {
    const maskedValue = maskZipCode(zipCode);

    setFieldValue("property.zipCode", maskedValue);
    if (maskedValue.length >= 9) {
      setCepLoading(true);

      AddressService.getAddressByZipCode(zipCode)
        .then((address) => {
          setFieldValue("property.city", address.city);
          setFieldValue("property.district", address.district);
          setFieldValue("property.state", address.state);
          setFieldValue("property.street", address.street);
          setCepLoading(false);
        })
        .catch(() => {
          setCepLoading(false);
        });
    }
  };

  useEffect(() => {
    if (selectedFromWallet) return;
    if (values.property.zipCode && values.property.zipCode.length >= 8) {
      onChangeCep(values.property.zipCode);
    }
  }, [values.property.zipCode, selectedFromWallet]);

  useEffect(() => {
    if (selectedFromWallet) {
      setNoComplement(values.property.hasComplement);
    }
  }, [selectedFromWallet, values.property.hasComplement]);

  return (
    <div style={{ padding: "0 0 0 10px" }}>
      <SectionLabel text="Dados do imóvel" />
      <Grid columns="equal">
        {resolvedEstateId && (
          <CapitalizationPropertyWalletSearchDropdown
            estateId={resolvedEstateId}
            setFieldValue={setFieldValue}
            values={values}
          />
        )}
        {selectedFromWallet && (
          <Grid.Row style={{ marginTop: "-6px" }}>
            <Grid.Column width={16}>
              <WalletTag>Imóvel da carteira — dados bloqueados para edição</WalletTag>
            </Grid.Column>
          </Grid.Row>
        )}
        <Grid.Row>
          <Grid.Column>
            <FieldLabel>CEP </FieldLabel>
            <Field
              name={`property.zipcode`}
              value={values.property.zipCode}
              onChange={(e: any) => {
                onChangeCep(e.target.value);
              }}
              component={Input}
              maxLength={9}
              fluid
              loading={cepLoading}
              disabled={isFieldLocked}
            />
          </Grid.Column>

          <Grid.Column>
            <FieldLabel>Estado</FieldLabel>
            <Field
              name={`property.state`}
              component={Input}
              value={values.property.state}
              onChange={(e: any) => {
                setFieldValue("property.state", e.target.value);
              }}
              fluid
              disabled={isFieldLocked}
            />
          </Grid.Column>

          <Grid.Column>
            <FieldLabel>Cidade</FieldLabel>
            <Field
              name={`property.city`}
              component={Input}
              value={values.property.city}
              onChange={(e: any) => {
                setFieldValue(`property.city`, e.target.value);
              }}
              fluid
              disabled={isFieldLocked}
            />
          </Grid.Column>
        </Grid.Row>
        <Grid.Row>
          <Grid.Column>
            <FieldLabel>Bairro</FieldLabel>
            <Field
              name={`property.district`}
              component={Input}
              value={values.property.district}
              onChange={(e: any) => {
                setFieldValue(`property.district`, e.target.value);
              }}
              fluid
              disabled={isFieldLocked}
            />
          </Grid.Column>

          <Grid.Column>
            <FieldLabel>Logradouro</FieldLabel>
            <Field
              name={`property.street`}
              component={Input}
              value={values.property.street}
              onChange={(e: any) => {
                setFieldValue(`property.street`, e.target.value);
              }}
              fluid
              disabled={isFieldLocked}
            />
          </Grid.Column>
        </Grid.Row>
        <Grid.Row>
          <Grid.Column width={4}>
            <FieldLabel>Número </FieldLabel>
            <Field
              name={`property.number`}
              component={Input}
              value={values.property.number}
              onChange={(e: any) => {
                setFieldValue(`property.number`, e.target.value);
              }}
              fluid
              disabled={isFieldLocked}
            />
          </Grid.Column>

          <Grid.Column width={5}>
            <FieldLabel>Complemento</FieldLabel>
            <Field
              name={`property.complement`}
              component={Input}
              value={values.property.complement}
              onChange={(e: any) => {
                setFieldValue(`property.complement`, e.target.value);
              }}
              disabled={isFieldLocked || noComplement}
            />
          </Grid.Column>

          <Grid.Column verticalAlign="middle" width={6}>
            <Checkbox
              label="Sem Complemento"
              checked={noComplement || false}
              disabled={isFieldLocked}
              onChange={(_e: any, data) => {
                if (data.checked) {
                  setNoComplement(true);
                  setFieldValue("property.hasComplement", true);
                } else {
                  setNoComplement(false);
                  setFieldValue("property.hasComplement", false);
                }
              }}
            />
          </Grid.Column>
        </Grid.Row>
        <Grid.Row>
          <Grid.Column width={6}>
            <FieldLabel>Tipo do Imóvel</FieldLabel>
            <DropdownNativo
              upward
              style={{ width: "100%" }}
              value={values.property.propertyType}
              name={`property.propertyType`}
              options={hirePropertyTypeOptions}
              placeholder={"Selecionar..."}
              onChange={(_e, { value }) =>
                setFieldValue("property.propertyType", value)
              }
              search
              selection
              clearable
              disabled={isFieldLocked}
            />
          </Grid.Column>
          <Grid.Column width={10}>
            <FieldLabel>Tipo da Locação</FieldLabel>
            <Grid.Column width={6}>
              <Radio
                label="Residencial"
                name={`locationType`}
                value={PurposesEnum.residential}
                checked={values.locationType === PurposesEnum.residential}
                onChange={(_, data) => {
                  setFieldValue(data.name!, data.value);
                }}
              />
            </Grid.Column>

            <Grid.Column width={4}>
              <Radio
                label="Comercial"
                name={`locationType`}
                value={PurposesEnum.commercial}
                checked={values.locationType === PurposesEnum.commercial}
                onChange={(_, data) => {
                  setFieldValue(data.name!, data.value);
                }}
              />
            </Grid.Column>
          </Grid.Column>
        </Grid.Row>
      </Grid>
    </div>
  );
};

export default CapitalizationPropertyStep;
